import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatMoney, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Plus, ShieldCheck, Clock, Trash2, X } from "lucide-react";

export default function Seller() {
  const { user, loading: authLoading } = useAuth();
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [categories, setCategories] = useState([]);
  const [makes, setMakes] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav("/login?next=/seller"); return; }
    if (user.role !== "seller" && user.role !== "admin") {
      toast.error("Seller access required"); nav("/dashboard"); return;
    }
    Promise.all([
      api.get("/seller/parts").then(r => setParts(r.data)),
      api.get("/catalog/categories").then(r => setCategories(r.data)),
      api.get("/catalog/makes").then(r => setMakes(r.data)),
    ]).finally(() => setLoading(false));
  }, [user, authLoading, nav]);

  const refresh = () => api.get("/seller/parts").then(r => setParts(r.data));

  const del = async (part_id) => {
    if (!window.confirm("Delete this listing?")) return;
    try {
      await api.delete(`/seller/parts/${part_id}`);
      toast.success("Part deleted");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (authLoading || loading) return <div className="flex justify-center py-32"><Loader2 className="animate-spin text-[#009A44]" size={28} /></div>;

  return (
    <main className="max-w-[1400px] mx-auto px-4 lg:px-8 py-12" data-testid="seller-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-widest text-[#009A44] mb-2">Seller console</div>
          <h1 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight">My inventory</h1>
        </div>
        <button onClick={() => setShowAdd(true)} className="btn-primary" data-testid="add-part-btn">
          <Plus size={16} /> List new part
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <Stat label="Total listings" value={parts.length} />
        <Stat label="Agent verified" value={parts.filter(p => p.verified_by_agent).length} color="#009A44" />
        <Stat label="Pending verification" value={parts.filter(p => !p.verified_by_agent).length} color="#FFD100" />
      </div>

      {parts.length === 0 ? (
        <div className="bg-white border border-[#E4E4E7] p-12 text-center">
          <h2 className="font-display text-2xl font-bold mb-2">No listings yet</h2>
          <p className="text-zinc-500 mb-5">Add your first part to start selling.</p>
          <button onClick={() => setShowAdd(true)} className="btn-primary"><Plus size={16} /> Add your first part</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="seller-parts">
          {parts.map(p => (
            <div key={p.part_id} className="bg-white border border-[#E4E4E7] p-4" data-testid={`seller-part-${p.part_id}`}>
              <div className="flex gap-3">
                <img src={p.image_url} alt="" className="w-20 h-20 object-cover bg-zinc-100" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm line-clamp-2">{p.name}</div>
                  <div className="text-xs text-zinc-500 mt-1">PN: {p.part_number}</div>
                  <div className="font-display font-extrabold text-lg mt-1">{formatMoney(p.price, p.currency)}</div>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between">
                {p.verified_by_agent ? (
                  <div className="bg-[#FFD100]/25 border border-[#FFD100] text-[#121212] text-[10px] uppercase font-bold px-2 py-1 flex items-center gap-1">
                    <ShieldCheck size={10} /> Verified
                  </div>
                ) : (
                  <div className="bg-zinc-100 border border-zinc-300 text-zinc-700 text-[10px] uppercase font-bold px-2 py-1 flex items-center gap-1">
                    <Clock size={10} /> Pending
                  </div>
                )}
                <button onClick={() => del(p.part_id)} className="text-[#EF3340] text-xs hover:underline flex items-center gap-1" data-testid={`delete-${p.part_id}`}>
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && <AddPartModal onClose={() => setShowAdd(false)} onCreated={refresh} categories={categories} makes={makes} />}
    </main>
  );
}

function Stat({ label, value, color = "#52525B" }) {
  return (
    <div className="bg-white border border-[#E4E4E7] p-5">
      <div className="text-[11px] uppercase tracking-widest font-bold mb-1" style={{ color }}>{label}</div>
      <div className="font-display text-4xl font-extrabold">{value}</div>
    </div>
  );
}

function AddPartModal({ onClose, onCreated, categories, makes }) {
  const [form, setForm] = useState({
    name: "", description: "", category: categories[0] || "Brakes", brand: "", part_number: "",
    price: "", image_url: "https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=800&q=80",
    condition: "New Aftermarket", stock: 10, weight_kg: 1.0,
    make: makes[0] || "Toyota", models: "", years: "2015,2018,2020",
  });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        name: form.name, description: form.description, category: form.category,
        brand: form.brand, part_number: form.part_number, price: parseFloat(form.price),
        image_url: form.image_url, condition: form.condition, stock: parseInt(form.stock) || 1,
        weight_kg: parseFloat(form.weight_kg) || 1.0,
        compatibility: {
          makes: [form.make],
          models: form.models.split(",").map(s => s.trim()).filter(Boolean),
          years: form.years.split(",").map(s => parseInt(s.trim())).filter(Boolean),
        },
      };
      await api.post("/seller/parts", payload);
      toast.success("Part submitted for agent verification");
      onCreated(); onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-auto" data-testid="add-part-modal">
      <div className="bg-white border-2 border-[#121212] max-w-2xl w-full my-8 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-2xl font-extrabold">List a new part</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          <Field label="Part name" value={form.name} onChange={v => update("name", v)} required testid="add-name" fullWidth />
          <Field label="Description" value={form.description} onChange={v => update("description", v)} required testid="add-desc" fullWidth textarea />
          <SelectField label="Category" value={form.category} onChange={v => update("category", v)} options={categories} testid="add-category" />
          <Field label="Brand" value={form.brand} onChange={v => update("brand", v)} required testid="add-brand" />
          <Field label="Part number" value={form.part_number} onChange={v => update("part_number", v)} required testid="add-pn" />
          <Field label="Price (USD)" type="number" step="0.01" value={form.price} onChange={v => update("price", v)} required testid="add-price" />
          <Field label="Stock" type="number" value={form.stock} onChange={v => update("stock", v)} testid="add-stock" />
          <Field label="Weight (kg)" type="number" step="0.1" value={form.weight_kg} onChange={v => update("weight_kg", v)} testid="add-weight" />
          <SelectField label="Condition" value={form.condition} onChange={v => update("condition", v)}
            options={["New OEM", "New Aftermarket", "Refurbished", "Used - Excellent", "Used - Good"]} testid="add-condition" />
          <Field label="Image URL" value={form.image_url} onChange={v => update("image_url", v)} testid="add-image" fullWidth />
          <SelectField label="Compatible make" value={form.make} onChange={v => update("make", v)} options={makes} testid="add-make" />
          <Field label="Models (comma sep)" value={form.models} onChange={v => update("models", v)} testid="add-models" />
          <Field label="Years (comma sep)" value={form.years} onChange={v => update("years", v)} testid="add-years" fullWidth />
          <div className="col-span-2 flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-outline-dark flex-1">Cancel</button>
            <button type="submit" disabled={loading} className="btn-primary flex-1" data-testid="submit-add-part">
              {loading ? <Loader2 size={14} className="animate-spin" /> : null}
              Submit for verification
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, fullWidth, textarea, step }) {
  return (
    <label className={`block ${fullWidth ? "col-span-2" : ""}`}>
      <span className="block text-[11px] font-bold uppercase tracking-widest text-zinc-600 mb-2">{label}</span>
      {textarea ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} required={required} rows={2} className="input-apa" data-testid={testid} />
      ) : (
        <input type={type} step={step} value={value} onChange={(e) => onChange(e.target.value)} required={required} className="input-apa" data-testid={testid} />
      )}
    </label>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-bold uppercase tracking-widest text-zinc-600 mb-2">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="input-apa" data-testid={testid}>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
